import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Clock3,
  Clapperboard,
  Eye,
  Filter,
  Grid2X2,
  Loader2,
  Play,
  RefreshCcw,
  Search,
  Shuffle,
  Sparkles,
  UserRound,
  X,
} from 'lucide-react';
import { ArtVideoPlayer } from './ArtVideoPlayer';
import { getRandomVideo, getVideoMeta, getVideoPage } from './api';
import type { PlayerVideo, VideoMeta } from './types';

const DISCOVER_PAGE_SIZE = 12;
const MAX_FRAME_LOADS = 2;
const MAX_FRAME_POSTER_CACHE = 80;

type ViewMode = 'random' | 'discover';

let activeFrameLoads = 0;
const pendingFrameLoads: Array<() => void> = [];
const framePosterCache = new Map<string, string>();

function rememberFramePoster(videoId: string, poster: string) {
  if (framePosterCache.has(videoId)) {
    framePosterCache.delete(videoId);
  }
  framePosterCache.set(videoId, poster);
  while (framePosterCache.size > MAX_FRAME_POSTER_CACHE) {
    const oldestKey = framePosterCache.keys().next().value;
    if (!oldestKey) {
      break;
    }
    framePosterCache.delete(oldestKey);
  }
}

function requestFrameLoadSlot(start: (release: () => void) => void) {
  let queued = true;
  let released = false;

  const release = () => {
    if (released) {
      return;
    }
    released = true;
    activeFrameLoads = Math.max(0, activeFrameLoads - 1);
    pendingFrameLoads.shift()?.();
  };

  const run = () => {
    if (!queued) {
      return;
    }
    queued = false;
    activeFrameLoads += 1;
    start(release);
  };

  if (activeFrameLoads < MAX_FRAME_LOADS) {
    run();
  } else {
    pendingFrameLoads.push(run);
  }

  return () => {
    if (queued) {
      queued = false;
      const index = pendingFrameLoads.indexOf(run);
      if (index >= 0) {
        pendingFrameLoads.splice(index, 1);
      }
      return;
    }
    release();
  };
}

interface VideoCoverProps {
  video: PlayerVideo;
  framePoster?: string;
  framePreviewPaused: boolean;
  onFramePosterReady: (videoId: string, poster: string) => void;
}

function VideoCover({ video, framePoster, framePreviewPaused, onFramePosterReady }: VideoCoverProps) {
  const [imageFailed, setImageFailed] = useState(!video.coverUrl);
  const [inView, setInView] = useState(false);
  const [wantsFramePreview, setWantsFramePreview] = useState(false);
  const [canLoadFrame, setCanLoadFrame] = useState(false);
  const [frameReady, setFrameReady] = useState(false);
  const [frameFailed, setFrameFailed] = useState(false);
  const [localFramePoster, setLocalFramePoster] = useState(() => framePoster || framePosterCache.get(video.id) || '');
  const coverRef = useRef<HTMLSpanElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const releaseFrameLoadRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setImageFailed(!video.coverUrl);
    setCanLoadFrame(false);
    setFrameReady(false);
    setFrameFailed(false);
    setWantsFramePreview(false);
    setLocalFramePoster(framePoster || framePosterCache.get(video.id) || '');
  }, [framePoster, video.coverUrl, video.id]);

  useEffect(() => {
    if (!coverRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { rootMargin: '80px 0px' },
    );
    observer.observe(coverRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (
      framePreviewPaused ||
      !inView ||
      !wantsFramePreview ||
      !imageFailed ||
      frameFailed ||
      frameReady ||
      !!localFramePoster ||
      !video.streamUrl
    ) {
      setCanLoadFrame(false);
      return undefined;
    }

    const cancel = requestFrameLoadSlot((release) => {
      releaseFrameLoadRef.current = release;
      setCanLoadFrame(true);
    });
    return () => {
      releaseFrameLoadRef.current = null;
      cancel();
    };
  }, [frameFailed, framePreviewPaused, frameReady, imageFailed, inView, localFramePoster, video.streamUrl, wantsFramePreview]);

  const releaseFrameLoad = () => {
    releaseFrameLoadRef.current?.();
    releaseFrameLoadRef.current = null;
  };

  const handleLoadedMetadata = () => {
    const element = videoRef.current;
    if (!element) {
      return;
    }
    try {
      element.currentTime = Math.min(0.1, Math.max(0, element.duration || 0.1));
    } catch {
      setFrameReady(true);
      releaseFrameLoad();
    }
  };

  const captureFramePoster = () => {
    const element = videoRef.current;
    if (!element || !element.videoWidth || !element.videoHeight) {
      return false;
    }
    try {
      const canvas = document.createElement('canvas');
      const maxWidth = 640;
      const scale = Math.min(1, maxWidth / element.videoWidth);
      canvas.width = Math.max(1, Math.round(element.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(element.videoHeight * scale));
      const context = canvas.getContext('2d');
      if (!context) {
        return false;
      }
      context.drawImage(element, 0, 0, canvas.width, canvas.height);
      const poster = canvas.toDataURL('image/jpeg', 0.74);
      rememberFramePoster(video.id, poster);
      setLocalFramePoster(poster);
      onFramePosterReady(video.id, poster);
      setCanLoadFrame(false);
      return true;
    } catch {
      return false;
    }
  };

  const handleFrameReady = () => {
    setFrameReady(true);
    if (!captureFramePoster()) {
      setFrameFailed(true);
      setCanLoadFrame(false);
    }
    releaseFrameLoad();
  };

  const handleFrameError = () => {
    setFrameFailed(true);
    setCanLoadFrame(false);
    releaseFrameLoad();
  };

  return (
    <span
      className="cover-wrap"
      ref={coverRef}
      onMouseEnter={() => setWantsFramePreview(true)}
      onFocus={() => setWantsFramePreview(true)}
    >
      {video.coverUrl && !imageFailed && (
        <img className="cover-media" src={video.coverUrl} alt={video.title} loading="lazy" onError={() => setImageFailed(true)} />
      )}
      {imageFailed && localFramePoster && <img className="cover-media" src={localFramePoster} alt={video.title} loading="lazy" />}
      {canLoadFrame && inView && imageFailed && !localFramePoster && !frameFailed && (
        <video
          ref={videoRef}
          className={`cover-media cover-video ${frameReady && !localFramePoster ? 'ready' : ''}`}
          src={`${video.streamUrl}#t=0.1`}
          muted
          playsInline
          crossOrigin="anonymous"
          preload="metadata"
          aria-hidden="true"
          onLoadedMetadata={handleLoadedMetadata}
          onLoadedData={handleFrameReady}
          onSeeked={handleFrameReady}
          onError={handleFrameError}
        />
      )}
      {(!video.coverUrl || imageFailed) && !localFramePoster && (!frameReady || frameFailed || !canLoadFrame) && (
        <span className={`cover-fallback ${canLoadFrame && !frameFailed ? 'loading-frame' : ''}`}>
          <span className="fallback-poster">
            <span>{video.categoryName || video.sourceName || '筱晶哥视频'}</span>
            <strong>{video.title}</strong>
          </span>
        </span>
      )}
      <span className="play-hover">
        <Play size={22} fill="currentColor" />
      </span>
      <span className="duration-pill">
        <Clock3 size={13} />
        {formatDuration(video.durationSeconds)}
      </span>
    </span>
  );
}

function formatDuration(seconds?: number) {
  if (seconds === undefined || seconds === null) {
    return '--:--';
  }
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

/**
 * 随机视频播放器主界面
 *
 * @author xiaojingge
 */
function App() {
  const [meta, setMeta] = useState<VideoMeta>({ sources: [], categories: [] });
  const [mode, setMode] = useState<ViewMode>('random');
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [currentVideo, setCurrentVideo] = useState<PlayerVideo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [playCount, setPlayCount] = useState(0);

  const [discoverCategoryId, setDiscoverCategoryId] = useState('');
  const [discoverVideos, setDiscoverVideos] = useState<PlayerVideo[]>([]);
  const [discoverPage, setDiscoverPage] = useState(1);
  const [discoverPages, setDiscoverPages] = useState(0);
  const [discoverTotal, setDiscoverTotal] = useState(0);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverLoadingMore, setDiscoverLoadingMore] = useState(false);
  const [discoverError, setDiscoverError] = useState('');
  const [discoverSearchDraft, setDiscoverSearchDraft] = useState('');
  const [discoverKeyword, setDiscoverKeyword] = useState('');
  const [selectedDiscoverVideo, setSelectedDiscoverVideo] = useState<PlayerVideo | null>(null);
  const [framePosters, setFramePosters] = useState<Record<string, string>>({});
  const [modalPlayerReady, setModalPlayerReady] = useState(false);
  const [modalPosterFailed, setModalPosterFailed] = useState(false);

  const retryTimerRef = useRef<number | undefined>(undefined);
  const autoRetryCountRef = useRef(0);
  const discoverSentinelRef = useRef<HTMLDivElement | null>(null);

  const visibleCategories = useMemo(
    () => meta.categories.filter((category) => !selectedSourceId || category.sourceId === selectedSourceId),
    [meta.categories, selectedSourceId],
  );

  const duplicateCategoryNames = useMemo(() => {
    const counts = meta.categories.reduce<Record<string, number>>((acc, category) => {
      acc[category.name] = (acc[category.name] || 0) + 1;
      return acc;
    }, {});
    return new Set(Object.entries(counts).filter(([, count]) => count > 1).map(([name]) => name));
  }, [meta.categories]);

  const formatCount = (count?: number) => {
    const value = count || 0;
    if (value >= 10000) {
      return `${(value / 10000).toFixed(value >= 100000 ? 0 : 1)}万`;
    }
    return String(value);
  };

  const getCategoryLabel = (category: VideoMeta['categories'][number]) => {
    if (duplicateCategoryNames.has(category.name) && category.sourceName) {
      return `${category.sourceName} / ${category.name}`;
    }
    return category.name;
  };

  const loadRandom = useCallback(
    async (nextFilters?: { sourceId?: string; categoryId?: string }, options: { clearBeforeLoad?: boolean } = {}) => {
      window.clearTimeout(retryTimerRef.current);
      if (options.clearBeforeLoad) {
        setCurrentVideo(null);
        autoRetryCountRef.current = 0;
      }
      setLoading(true);
      setError('');
      try {
        const video = await getRandomVideo({
          sourceId: nextFilters?.sourceId ?? selectedSourceId,
          categoryId: nextFilters?.categoryId ?? selectedCategoryId,
        });
        setCurrentVideo(video);
        setPlayCount((count) => count + 1);
      } catch (ex) {
        if (options.clearBeforeLoad) {
          setCurrentVideo(null);
        }
        setError(ex instanceof Error ? ex.message : '随机视频加载失败');
      } finally {
        setLoading(false);
      }
    },
    [selectedCategoryId, selectedSourceId],
  );

  const loadDiscover = useCallback(
    async (page = 1) => {
      const firstPage = page === 1;
      if (firstPage) {
        setDiscoverLoading(true);
      } else {
        setDiscoverLoadingMore(true);
      }
      setDiscoverError('');
      try {
        const result = await getVideoPage({
          current: page,
          size: DISCOVER_PAGE_SIZE,
          categoryId: discoverCategoryId,
          keyword: discoverKeyword,
        });
        setDiscoverVideos((videos) => {
          if (firstPage) {
            return result.records;
          }
          const existingIds = new Set(videos.map((video) => video.id));
          return [...videos, ...result.records.filter((video) => !existingIds.has(video.id))];
        });
        setDiscoverPage(result.current);
        setDiscoverPages(result.pages);
        setDiscoverTotal(result.total);
      } catch (ex) {
        if (firstPage) {
          setDiscoverVideos([]);
          setDiscoverTotal(0);
          setDiscoverPages(0);
        }
        setDiscoverError(ex instanceof Error ? ex.message : '视频列表加载失败');
      } finally {
        setDiscoverLoading(false);
        setDiscoverLoadingMore(false);
      }
    },
    [discoverCategoryId, discoverKeyword],
  );

  useEffect(() => {
    let mounted = true;
    getVideoMeta()
      .then((data) => {
        if (mounted) {
          setMeta(data);
        }
      })
      .catch((ex) => setError(ex instanceof Error ? ex.message : '筛选数据加载失败'));

    setLoading(true);
    getRandomVideo({})
      .then((video) => {
        if (mounted) {
          setCurrentVideo(video);
          setPlayCount((count) => count + 1);
        }
      })
      .catch((ex) => {
        if (mounted) {
          setError(ex instanceof Error ? ex.message : '随机视频加载失败');
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
      window.clearTimeout(retryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (mode === 'discover') {
      loadDiscover(1);
    }
  }, [discoverCategoryId, discoverKeyword, loadDiscover, mode]);

  useEffect(() => {
    if (
      mode !== 'discover' ||
      !discoverSentinelRef.current ||
      discoverLoading ||
      discoverLoadingMore ||
      discoverPage >= discoverPages
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadDiscover(Number(discoverPage) + 1);
        }
      },
      { rootMargin: '360px 0px' },
    );
    observer.observe(discoverSentinelRef.current);
    return () => observer.disconnect();
  }, [discoverLoading, discoverLoadingMore, discoverPage, discoverPages, loadDiscover, mode]);

  const handleSourceSelect = (sourceId: string) => {
    setSelectedSourceId(sourceId);
    setSelectedCategoryId('');
    loadRandom({ sourceId, categoryId: '' }, { clearBeforeLoad: true });
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    loadRandom({ sourceId: selectedSourceId, categoryId }, { clearBeforeLoad: true });
  };

  const handleDiscoverSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDiscoverKeyword(discoverSearchDraft.trim());
  };

  const handleFramePosterReady = useCallback((videoId: string, poster: string) => {
    setFramePosters((posters) => {
      if (posters[videoId]) {
        return posters;
      }
      const next = { ...posters, [videoId]: poster };
      const keys = Object.keys(next);
      while (keys.length > MAX_FRAME_POSTER_CACHE) {
        const oldestKey = keys.shift();
        if (oldestKey) {
          delete next[oldestKey];
        }
      }
      return next;
    });
  }, []);

  const closeDiscoverModal = useCallback(() => {
    setSelectedDiscoverVideo(null);
  }, []);

  useEffect(() => {
    setModalPlayerReady(false);
    setModalPosterFailed(false);
  }, [selectedDiscoverVideo?.id]);

  useEffect(() => {
    if (!selectedDiscoverVideo) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDiscoverModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeDiscoverModal, selectedDiscoverVideo]);

  const handlePlayerError = useCallback(() => {
    autoRetryCountRef.current += 1;
    if (autoRetryCountRef.current > 3) {
      window.clearTimeout(retryTimerRef.current);
      setCurrentVideo(null);
      setError('连续多个视频播放失败，请检查接口跨域、HTTPS地址或稍后手动重试。');
      return;
    }
    setError(`当前视频源不可用，正在自动切换下一条（${autoRetryCountRef.current}/3）`);
    retryTimerRef.current = window.setTimeout(() => {
      loadRandom();
    }, 900);
  }, [loadRandom]);

  const selectedDiscoverPoster = selectedDiscoverVideo
    ? framePosters[selectedDiscoverVideo.id] || selectedDiscoverVideo.coverUrl
    : undefined;

  const renderRandomMode = () => (
    <section className="hero-grid">
      <aside className="filter-panel crayon-card">
        <div className="section-title">
          <span className="icon-tile blue">
            <Filter size={18} />
          </span>
          <div>
            <h2>筛选</h2>
            <span>Source & Category</span>
          </div>
        </div>

        <div className="filter-block">
          <label>来源</label>
          <div className="chip-list">
            <button
              className={`filter-chip ${selectedSourceId === '' ? 'selected' : ''}`}
              type="button"
              onClick={() => handleSourceSelect('')}
            >
              全部
            </button>
            {meta.sources.map((source) => (
              <button
                key={source.id}
                className={`filter-chip ${selectedSourceId === source.id ? 'selected' : ''}`}
                type="button"
                onClick={() => handleSourceSelect(source.id)}
              >
                {source.icon && <span>{source.icon}</span>}
                {source.name}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-block">
          <label>分类</label>
          <div className="chip-list">
            <button
              className={`filter-chip ${selectedCategoryId === '' ? 'selected' : ''}`}
              type="button"
              onClick={() => handleCategorySelect('')}
            >
              随机
            </button>
            {visibleCategories.map((category) => (
              <button
                key={category.id}
                className={`filter-chip ${selectedCategoryId === category.id ? 'selected' : ''}`}
                type="button"
                onClick={() => handleCategorySelect(category.id)}
              >
                {category.icon && <span>{category.icon}</span>}
                {category.name}
              </button>
            ))}
          </div>
        </div>

        <div className="stats-card">
          <div>
            <strong>{meta.sources.length}</strong>
            <span>来源</span>
          </div>
          <div>
            <strong>{meta.categories.length}</strong>
            <span>分类</span>
          </div>
          <div>
            <strong>{playCount}</strong>
            <span>本次播放</span>
          </div>
        </div>
      </aside>

      <section className="player-panel crayon-card">
        <div className="player-header">
          <div className="now-playing">
            <span className="icon-tile red">
              <Play size={18} fill="currentColor" />
            </span>
            <div>
              <span>正在播放</span>
              <h2>{currentVideo?.title || '准备随机视频'}</h2>
            </div>
          </div>
          <div className="badge-row">
            {currentVideo?.sourceName && <span className="crayon-badge yellow">{currentVideo.sourceName}</span>}
            {currentVideo?.categoryName && <span className="crayon-badge green">{currentVideo.categoryName}</span>}
            <span className="crayon-badge purple">{formatDuration(currentVideo?.durationSeconds)}</span>
          </div>
        </div>

        <div className="video-frame">
          {currentVideo ? (
            <ArtVideoPlayer
              key={currentVideo.id}
              title={currentVideo.title}
              url={currentVideo.streamUrl}
              poster={currentVideo.coverUrl}
              onEnded={() => loadRandom(undefined, { clearBeforeLoad: true })}
              onError={handlePlayerError}
            />
          ) : (
            <div className="empty-player">
              <Sparkles size={44} />
              <span>{loading ? '正在抽取视频' : '暂无视频'}</span>
            </div>
          )}
        </div>

        {error && <div className="notice warning">{error}</div>}

        {currentVideo ? (
          <footer className="video-meta">
            <div>
              <span>作者</span>
              <strong>{currentVideo.authorName || '未知'}</strong>
            </div>
            <div>
              <span>清晰度</span>
              <strong>{currentVideo.resolution || '自适应'}</strong>
            </div>
            <div>
              <span>预览</span>
              <strong>{currentVideo.viewCount || 0}</strong>
            </div>
          </footer>
        ) : (
          <div className="empty-meta">换个来源或分类试试，当前筛选条件下还没有可播放内容。</div>
        )}
      </section>
    </section>
  );

  const renderDiscoverMode = () => (
    <section className="discover-page">
      <div className="discover-heading">
        <div>
          <span className="discover-eyebrow">Discover</span>
          <h2>发现视频</h2>
          <p>{discoverTotal > 0 ? `${formatCount(discoverTotal)} 条内容` : '全部公开视频'}</p>
        </div>
        <form className="discover-search" onSubmit={handleDiscoverSearch}>
          <Search size={18} />
          <input
            value={discoverSearchDraft}
            onChange={(event) => setDiscoverSearchDraft(event.target.value)}
            placeholder="搜索标题、作者"
          />
          <button type="submit" aria-label="搜索">
            <Search size={17} />
          </button>
        </form>
      </div>

      <nav className="category-tabs" aria-label="视频分类">
        <button
          className={`category-tab ${discoverCategoryId === '' ? 'active' : ''}`}
          type="button"
          onClick={() => setDiscoverCategoryId('')}
        >
          全部
        </button>
        {meta.categories.map((category) => (
          <button
            key={category.id}
            className={`category-tab ${discoverCategoryId === category.id ? 'active' : ''}`}
            type="button"
            onClick={() => setDiscoverCategoryId(category.id)}
            title={getCategoryLabel(category)}
          >
            {category.icon && <span>{category.icon}</span>}
            {getCategoryLabel(category)}
          </button>
        ))}
      </nav>

      {discoverError && <div className="discover-notice">{discoverError}</div>}

      {discoverLoading ? (
        <section className="video-feed" aria-label="视频加载中">
          {Array.from({ length: 8 }).map((_, index) => (
            <div className="video-card skeleton-card" key={index}>
              <div />
              <span />
              <strong />
            </div>
          ))}
        </section>
      ) : discoverVideos.length > 0 ? (
        <>
          <section className="video-feed" aria-label="视频列表">
            {discoverVideos.map((video, index) => (
              <button
                className={`video-card ${index === 0 && !discoverCategoryId && !discoverKeyword ? 'featured-card' : ''}`}
                key={video.id}
                type="button"
                onClick={() => setSelectedDiscoverVideo(video)}
              >
                <VideoCover
                  video={video}
                  framePoster={framePosters[video.id]}
                  framePreviewPaused={Boolean(selectedDiscoverVideo)}
                  onFramePosterReady={handleFramePosterReady}
                />
                <strong>{video.title}</strong>
                <span className="card-subline">
                  <span>
                    <UserRound size={14} />
                    {video.authorName || video.sourceName || '未知作者'}
                  </span>
                  <span>
                    <Eye size={14} />
                    {formatCount(video.viewCount)}
                  </span>
                </span>
              </button>
            ))}
          </section>
          <div className="load-sentinel" ref={discoverSentinelRef}>
            {discoverLoadingMore && (
              <span>
                <Loader2 size={18} />
                加载中
              </span>
            )}
            {!discoverLoadingMore && discoverPage >= discoverPages && discoverVideos.length > 0 && <span>已经到底了</span>}
          </div>
        </>
      ) : (
        <div className="discover-empty">
          <Sparkles size={42} />
          <strong>暂无视频</strong>
          <span>当前分类还没有可展示内容</span>
        </div>
      )}
    </section>
  );

  return (
    <main className={`app-shell ${mode === 'discover' ? 'discover-shell' : ''}`}>
      <header className="topbar">
        <div className="brand">
          <span className="brand-icon">
            <Clapperboard size={24} strokeWidth={2.8} />
          </span>
          <div>
            <h1>筱晶哥视频</h1>
            <p>{mode === 'random' ? 'Random Video Player' : 'Video Discovery'}</p>
          </div>
        </div>
        <div className="topbar-actions">
          <div className="mode-switch" role="tablist" aria-label="展示模式">
            <button className={mode === 'random' ? 'active' : ''} type="button" onClick={() => setMode('random')}>
              <Shuffle size={17} />
              随机播放
            </button>
            <button className={mode === 'discover' ? 'active' : ''} type="button" onClick={() => setMode('discover')}>
              <Grid2X2 size={17} />
              发现首页
            </button>
          </div>
          {mode === 'random' && (
            <button
              className="crayon-btn crayon-btn-small"
              type="button"
              onClick={() => loadRandom(undefined, { clearBeforeLoad: true })}
              disabled={loading}
            >
              <RefreshCcw size={18} />
              换一条
            </button>
          )}
        </div>
      </header>

      {mode === 'random' ? renderRandomMode() : renderDiscoverMode()}

      {selectedDiscoverVideo && (
        <div
          className="player-modal"
          role="dialog"
          aria-modal="true"
          aria-label={selectedDiscoverVideo.title}
          onClick={closeDiscoverModal}
        >
          <div className="modal-backdrop" />
          <section className="modal-panel" onClick={(event) => event.stopPropagation()}>
            <button className="modal-close" type="button" onClick={closeDiscoverModal} aria-label="关闭">
              <X size={21} />
            </button>
            <div className="modal-player-frame">
              {!modalPlayerReady && (
                <div className="modal-loading-cover">
                  {selectedDiscoverPoster && !modalPosterFailed ? (
                    <img src={selectedDiscoverPoster} alt={selectedDiscoverVideo.title} onError={() => setModalPosterFailed(true)} />
                  ) : (
                    <span>
                      <Loader2 size={26} />
                      正在加载视频
                    </span>
                  )}
                </div>
              )}
              <ArtVideoPlayer
                key={selectedDiscoverVideo.id}
                title={selectedDiscoverVideo.title}
                url={selectedDiscoverVideo.streamUrl}
                poster={selectedDiscoverPoster}
                onEnded={() => undefined}
                onError={() => {
                  setModalPlayerReady(true);
                  setDiscoverError('当前视频源不可用，请稍后重试。');
                }}
                onReady={() => setModalPlayerReady(true)}
              />
            </div>
            <div className="modal-meta">
              <span>{selectedDiscoverVideo.categoryName || selectedDiscoverVideo.sourceName || '视频'}</span>
              <h3>{selectedDiscoverVideo.title}</h3>
              <p>{selectedDiscoverVideo.description || selectedDiscoverVideo.authorName || '筱晶哥视频'}</p>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

export default App;
